import { pilot } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

type Request = {
  id: PilotId;
};

type ServerResponse = {
  results: {
    id: PilotId;
    uf_field_id?: string;
  }[];
};

/**
 * Resolves a pilot field id to its unified field id, when the field is
 * UF-backed. Ask AI cites fields by pilot id, while `document-information`
 * identifies a UF-backed field only by `uf_field_id` — so a consumer holding a
 * pilot id needs this to address the field in that payload. See EKP-57887.
 *
 * Deliberately separate from `getFields`, which resolves each result's
 * `DataFieldType` and skips any field whose type it can't map (a UF `FILE`, for
 * one) — that filtering would drop the very fields this lookup exists to
 * resolve.
 */
export const getFieldUfId = async ({
  id,
}: Request): Promise<Nullable<string>> => {
  const serverResponse: ServerResponse = await pilot.get('/field/', {
    params: { ids: String(id) },
  });

  return mapResponse(serverResponse, id);
};

const mapResponse = (
  serverResponse: ServerResponse,
  id: PilotId,
): Nullable<string> => {
  const field = (serverResponse.results ?? []).find(
    (result) => String(result.id) === String(id),
  );

  return field?.uf_field_id ?? null;
};
